const OPERATOR_ORDER = [
  { key: 'beeline', label: 'Билайн' },
  { key: 'mts', label: 'МТС' },
  { key: 'megafon', label: 'Мегафон' },
  { key: 't2', label: 'Теле2' },
  { key: 'yota', label: 'Yota' },
];

const hasPositiveAllocationWeights = (weights) =>
  Object.values(weights || {}).some((value) => Number(value || 0) > 0);

const pickAllocationWeights = (...candidates) => {
  for (const candidate of candidates) {
    if (hasPositiveAllocationWeights(candidate)) {
      return candidate;
    }
  }
  return {};
};

const allocateIntegerByWeights = (total, weights) => {
  const normalizedTotal = Math.max(0, Math.trunc(Number(total) || 0));
  if (normalizedTotal === 0) {
    return {};
  }

  const entries = Object.entries(weights || {})
    .map(([key, value]) => [key, Math.max(0, Number(value) || 0)])
    .filter(([, value]) => value > 0);

  if (entries.length === 0) {
    return {};
  }

  const totalWeight = entries.reduce((sum, [, value]) => sum + value, 0);
  if (totalWeight <= 0) {
    return {};
  }

  const exactAllocations = entries.map(([key, value]) => {
    const exact = (normalizedTotal * value) / totalWeight;
    const base = Math.floor(exact);
    return {
      key,
      value,
      base,
      fraction: exact - base,
    };
  });

  let remaining = normalizedTotal - exactAllocations.reduce((sum, item) => sum + item.base, 0);
  exactAllocations.sort((a, b) => {
    if (b.fraction !== a.fraction) return b.fraction - a.fraction;
    if (b.value !== a.value) return b.value - a.value;
    return String(a.key).localeCompare(String(b.key), 'ru');
  });

  const result = exactAllocations.reduce((acc, item) => ({
    ...acc,
    [item.key]: item.base,
  }), {});

  for (let index = 0; index < exactAllocations.length && remaining > 0; index += 1) {
    result[exactAllocations[index].key] += 1;
    remaining -= 1;
  }

  return result;
};

export const mapCityToGeoKey = (rawCity) => {
  const value = String(rawCity || '').trim().toLowerCase();
  if (
    value.includes('петербург')
    || value.includes('питер')
    || value.includes('спб')
    || value.includes('spb')
    || value.includes('saint petersburg')
  ) {
    return 'spb';
  }
  if (
    value.includes('москва')
    || value.includes('мск')
    || value.includes('msk')
    || value.includes('moscow')
  ) {
    return 'msk';
  }
  return null;
};

export const mapCityGeoKeyToLabel = (geoKey) => {
  if (geoKey === 'spb') return 'СПБ';
  if (geoKey === 'msk') return 'МСК';
  return null;
};

export const mapOperatorNameToKey = (rawOperator, resolveOperatorByTemplates, operatorTemplates = []) => {
  const resolved = typeof resolveOperatorByTemplates === 'function'
    ? resolveOperatorByTemplates(rawOperator, operatorTemplates)
    : String(rawOperator || '').trim();
  const value = String(resolved || '').trim().toLowerCase();
  if (!value) return null;
  if (value.includes('beeline') || value.includes('билайн')) return 'beeline';
  if (value.includes('megafon') || value.includes('мегафон')) return 'megafon';
  if (value.includes('tele2') || value.includes('т2') || value.includes('t2') || value.includes('теле2')) return 't2';
  if (value.includes('yota')) return 'yota';
  if (value.includes('mts') || value.includes('мтс')) return 'mts';
  return null;
};

export const computeConnectionForSaleModems = (connection) => {
  if (connection == null) return 0;
  const fromApi = connection.forSaleModems ?? connection.for_sale_modems;
  if (fromApi != null && Number.isFinite(Number(fromApi))) {
    return Math.max(0, Number(fromApi));
  }
  const free = Math.max(0, Number(connection?.free_modems) || 0);
  const unavailable = Math.max(0, Number(connection?.unavailable_modems) || 0);
  const reserve = Math.max(0, Number(connection?.reserve_modems_count) || 0);
  return Math.max(0, free - unavailable - reserve);
};

const createOperatorCounters = () => OPERATOR_ORDER.reduce((acc, item) => {
  acc[item.key] = {
    forSaleSum: 0,
    forSaleSpbSum: 0,
    forSaleMskSum: 0,
    spbFree: 0,
    mskFree: 0,
    total: 0,
    spbTotal: 0,
    mskTotal: 0,
    freeSum: 0,
    reserveSum: 0,
    reserveSpbSum: 0,
    reserveMskSum: 0,
  };
  return acc;
}, {});

const isPortKrakenLive = (port) => {
  const value = port?.kraken_connection_live;
  if (value === false || value === 0) return false;
  return true;
};

const isPortFreeByLoad = (port) => {
  const loadRaw = port?.overall_load_percentage ?? port?.utilization;
  const hasLoadValue = loadRaw !== undefined && loadRaw !== null;
  const loadValue = hasLoadValue ? Number(loadRaw) : NaN;
  return hasLoadValue && Number.isFinite(loadValue) && loadValue < 0.1;
};

/**
 * Считает «В продаже» по операторам и городам так же, как Dashboard / ServerSummaryBlock:
 * свободные − недоступные − резерв (настройка подключения), с пропорциональным распределением по операторам.
 */
export const buildForSaleStats = ({
  ports = [],
  connections = [],
  operatorTemplates = [],
  resolveOperatorByTemplates = null,
  isPortEligible = isPortKrakenLive,
  dashboardStats = null,
} = {}) => {
  const counters = createOperatorCounters();
  const activeConnections = Array.isArray(connections)
    ? connections.filter((conn) => conn && conn.is_active !== false)
    : [];

  const eligiblePorts = (Array.isArray(ports) ? ports : []).filter((port) => {
    if (!port || typeof port !== 'object') return false;
    return typeof isPortEligible === 'function' ? isPortEligible(port) : true;
  });

  if (eligiblePorts.length > 0 && activeConnections.length > 0) {
    const connModemsByOp = {};
    const connFreeByOp = {};
    const connFreeByOpSpb = {};
    const connFreeByOpMsk = {};

    const incrementConnectionOperatorCounter = (targetMap, connectionId, operatorKey) => {
      if (!Number.isFinite(connectionId) || connectionId <= 0 || !operatorKey) return;
      if (!targetMap[connectionId]) targetMap[connectionId] = {};
      targetMap[connectionId][operatorKey] = (targetMap[connectionId][operatorKey] || 0) + 1;
    };

    eligiblePorts.forEach((port) => {
      const operatorKey = mapOperatorNameToKey(
        port.operator || port.mobile_operator || port.selected_operator,
        resolveOperatorByTemplates,
        operatorTemplates
      );
      if (!operatorKey || !counters[operatorKey]) return;

      const cityKey = mapCityToGeoKey(port.city || port.connection_city || port.server_city || port.geo);
      const isFree = isPortFreeByLoad(port);

      counters[operatorKey].total += 1;
      if (isFree) counters[operatorKey].freeSum += 1;

      if (cityKey === 'spb') {
        counters[operatorKey].spbTotal += 1;
        if (isFree) counters[operatorKey].spbFree += 1;
      }
      if (cityKey === 'msk') {
        counters[operatorKey].mskTotal += 1;
        if (isFree) counters[operatorKey].mskFree += 1;
      }

      const connectionId = Number(port.connection_id ?? port.kraken_connection_id);
      if (Number.isFinite(connectionId) && connectionId > 0) {
        incrementConnectionOperatorCounter(connModemsByOp, connectionId, operatorKey);
        if (isFree) {
          incrementConnectionOperatorCounter(connFreeByOp, connectionId, operatorKey);
          if (cityKey === 'spb') incrementConnectionOperatorCounter(connFreeByOpSpb, connectionId, operatorKey);
          if (cityKey === 'msk') incrementConnectionOperatorCounter(connFreeByOpMsk, connectionId, operatorKey);
        }
      }
    });

    activeConnections.forEach((connection) => {
      const connectionId = Number(connection?.id);
      if (!Number.isFinite(connectionId) || connectionId <= 0) {
        return;
      }

      const forSaleModems = computeConnectionForSaleModems(connection);
      if (forSaleModems === 0) {
        return;
      }

      const overallWeights = pickAllocationWeights(
        connFreeByOp[connectionId],
        connModemsByOp[connectionId]
      );
      const overallAllocation = allocateIntegerByWeights(forSaleModems, overallWeights);
      Object.entries(overallAllocation).forEach(([operatorKey, value]) => {
        if (counters[operatorKey]) {
          counters[operatorKey].forSaleSum += value;
        }
      });

      const connectionCityKey = mapCityToGeoKey(connection?.city);
      if (connectionCityKey === 'spb' || connectionCityKey === 'msk') {
        const cityWeights = connectionCityKey === 'spb'
          ? pickAllocationWeights(connFreeByOpSpb[connectionId], connFreeByOp[connectionId], connModemsByOp[connectionId])
          : pickAllocationWeights(connFreeByOpMsk[connectionId], connFreeByOp[connectionId], connModemsByOp[connectionId]);

        const cityAllocation = allocateIntegerByWeights(forSaleModems, cityWeights);
        Object.entries(cityAllocation).forEach(([operatorKey, value]) => {
          if (!counters[operatorKey]) {
            return;
          }
          if (connectionCityKey === 'spb') {
            counters[operatorKey].forSaleSpbSum += value;
          } else {
            counters[operatorKey].forSaleMskSum += value;
          }
        });
      }
    });
  } else if (dashboardStats && typeof dashboardStats === 'object') {
    const connReserve = {};
    activeConnections.forEach((connection) => {
      if (connection && connection.id != null) {
        connReserve[connection.id] = Number(connection.reserve_modems_count) || 0;
      }
    });

    const operatorsMap = dashboardStats.operators && typeof dashboardStats.operators === 'object'
      ? dashboardStats.operators
      : {};
    Object.entries(operatorsMap).forEach(([operatorName, count]) => {
      const operatorKey = mapOperatorNameToKey(operatorName, resolveOperatorByTemplates, operatorTemplates);
      if (!operatorKey || !counters[operatorKey]) return;
      counters[operatorKey].total += Number(count || 0);
    });

    const freeByCity = dashboardStats.free_operators_by_city && typeof dashboardStats.free_operators_by_city === 'object'
      ? dashboardStats.free_operators_by_city
      : {};
    Object.entries(freeByCity).forEach(([cityName, operatorsByCity]) => {
      const cityKey = mapCityToGeoKey(cityName);
      if (!cityKey || !operatorsByCity || typeof operatorsByCity !== 'object') return;
      Object.entries(operatorsByCity).forEach(([operatorName, count]) => {
        const operatorKey = mapOperatorNameToKey(operatorName, resolveOperatorByTemplates, operatorTemplates);
        if (!operatorKey || !counters[operatorKey]) return;
        if (cityKey === 'spb') counters[operatorKey].spbFree += Number(count || 0);
        if (cityKey === 'msk') counters[operatorKey].mskFree += Number(count || 0);
      });
    });

    const totalModemsAll = OPERATOR_ORDER.reduce((sum, { key }) => sum + (counters[key].total || 0), 0);
    const totalReserveAll = Object.values(connReserve).reduce((sum, value) => sum + (Number(value) || 0), 0);
    OPERATOR_ORDER.forEach(({ key }) => {
      const total = counters[key].total || 0;
      if (totalModemsAll > 0 && totalReserveAll > 0 && total > 0) {
        counters[key].reserveSum = Math.round((totalReserveAll * total) / totalModemsAll);
      }
      counters[key].freeSum = (counters[key].spbFree || 0) + (counters[key].mskFree || 0);
    });

    OPERATOR_ORDER.forEach(({ key }) => {
      const total = counters[key].total || 0;
      const reserve = counters[key].reserveSum || 0;
      const spbTotal = counters[key].spbTotal || 0;
      const mskTotal = counters[key].mskTotal || 0;
      if (total > 0 && reserve > 0) {
        counters[key].reserveSpbSum = spbTotal > 0 ? Math.round((reserve * spbTotal) / total) : 0;
        counters[key].reserveMskSum = mskTotal > 0 ? Math.round((reserve * mskTotal) / total) : 0;
      } else {
        counters[key].reserveSpbSum = 0;
        counters[key].reserveMskSum = 0;
      }
      counters[key].forSaleSum = Math.max(0, (counters[key].freeSum || 0) - (counters[key].reserveSum || 0));
      counters[key].forSaleSpbSum = Math.max(0, (counters[key].spbFree || 0) - (counters[key].reserveSpbSum || 0));
      counters[key].forSaleMskSum = Math.max(0, (counters[key].mskFree || 0) - (counters[key].reserveMskSum || 0));
    });
  }

  const forSaleOperators = {};
  const forSaleOperatorsByCity = {
    МСК: {},
    СПБ: {},
  };

  OPERATOR_ORDER.forEach(({ key, label }) => {
    const counter = counters[key];
    if (!counter) return;

    if (counter.forSaleSum > 0) {
      forSaleOperators[label] = counter.forSaleSum;
    }
    if (counter.forSaleMskSum > 0) {
      forSaleOperatorsByCity.МСК[label] = counter.forSaleMskSum;
    }
    if (counter.forSaleSpbSum > 0) {
      forSaleOperatorsByCity.СПБ[label] = counter.forSaleSpbSum;
    }
  });

  const forSaleTotal = Object.values(forSaleOperators).reduce((sum, value) => sum + Number(value || 0), 0);

  const cleanedForSaleOperatorsByCity = Object.entries(forSaleOperatorsByCity).reduce((acc, [cityName, cityOperators]) => {
    const entries = Object.entries(cityOperators).filter(([, count]) => Number(count || 0) > 0);
    if (entries.length > 0) {
      acc[cityName] = Object.fromEntries(entries);
    }
    return acc;
  }, {});

  return {
    for_sale_operators: forSaleOperators,
    for_sale_operators_by_city: cleanedForSaleOperatorsByCity,
    for_sale_total: forSaleTotal,
  };
};
